import dayjs from "dayjs";

// Shared bits for the appraisal and survey screens (Settings > Appraisals & Surveys, the
// Appraisals page, an employee's Appraisals tab).

export const STATUS_META = {
    awaitingOfficer: { label: "Self-assessment", color: "blue", hint: "Waiting for the officer's self-assessment" },
    awaitingReviewer: { label: "With reviewer", color: "gold", hint: "Waiting for the reviewer" },
    awaitingSignOff: { label: "Officer to sign", color: "purple", hint: "Reviewed - waiting for the officer to sign" },
    completed: { label: "Completed", color: "green", hint: "Signed by both" },
    cancelled: { label: "Cancelled", color: "default", hint: "Cancelled" },
};

export const SCORING_LABEL = {
    both: "Ratings and comments",
    ratings: "Ratings only",
    comments: "Comments only",
};

export const QUESTION_TYPES = {
    rating: "Rating",
    comment: "Written answer",
    text: "Short answer",
    yesno: "Yes / No",
    choice: "Dropdown / choice",
    checkbox: "Checkbox",
    number: "Number",
    date: "Date",
};

// which question types a template's scoring style allows (mirrors the server's utils/formAnswers)
export const TYPES_FOR_SCORING = {
    both: ["rating", "comment", "text", "yesno", "choice", "checkbox", "number", "date"],
    ratings: ["rating", "yesno", "choice", "checkbox", "number", "date"],
    comments: ["comment", "text", "yesno", "choice", "checkbox", "number", "date"],
};

// whether an answer counts as answered (a required checkbox has to be checked)
export const isAnswered = (q, a) => {
    switch (q?.type) {
        case "rating": return Number.isInteger(a?.rating);
        case "yesno": return typeof a?.answer === "boolean";
        case "checkbox": return a?.answer === true;
        case "number": return a?.number !== undefined && a?.number !== null && a?.number !== "" && Number.isFinite(Number(a.number));
        case "date": return !!a?.date;
        case "choice": return !!a?.choice;
        default: return !!String(a?.comment || "").trim();
    }
};

// an answer in words (mirrors the server's answerText)
export const answerText = (q, a, scale) => {
    if (!a) return "";
    const comment = a.comment && !["comment", "text"].includes(q?.type) ? ` - ${a.comment}` : "";
    switch (q?.type) {
        case "rating": return Number.isInteger(a.rating) ? `${a.rating}/${scale?.max || 5}${scale?.labels?.[a.rating - 1] ? ` - ${scale.labels[a.rating - 1]}` : ""}${comment}` : a.comment || "";
        case "yesno": return `${a.answer === true ? "Yes" : a.answer === false ? "No" : ""}${comment}`.replace(/^ - /, "");
        case "checkbox": return a.answer === true ? "Checked" : a.answer === false ? "Not checked" : "";
        case "number": return a.number !== undefined && a.number !== null ? String(a.number) : "";
        case "date": return a.date ? dayjs(a.date).format("DD/MM/YYYY") : "";
        case "choice": return a.choice || "";
        default: return a.comment || "";
    }
};

export const ANSWERED_BY = {
    reviewer: "Reviewer",
    officer: "Officer",
    both: "Both",
};

// a two-way survey's questions: the customer / supplier, our side, or both
// (a one-way survey's are all answered by the person surveyed)
export const SURVEY_ANSWERED_BY = {
    respondent: "Them",
    us: "Us",
    both: "Both",
};

export const CATEGORY_LABEL = {
    appraisal: "Appraisal",
    survey: "Survey",
};

// older appraisal templates have no category
export const categoryOf = (template) => (template?.category === "survey" ? "survey" : "appraisal");

export const defaultAnsweredBy = (category) => (category === "survey" ? "respondent" : "reviewer");

export const DEFAULT_LABELS = {
    3: ["Below expectations", "Meets expectations", "Exceeds expectations"],
    4: ["Unsatisfactory", "Developing", "Good", "Strong"],
    5: ["Poor", "Needs improvement", "Good", "Very good", "Excellent"],
};

export const labelsFor = (max, current = []) => {
    const base = DEFAULT_LABELS[max] || Array.from({ length: max }, (_, i) => String(i + 1));
    return Array.from({ length: max }, (_, i) => current[i] || base[i]);
};

export const describeRepeat = (months) =>
    !months ? "One-off" : months === 12 ? "Every year" : `Every ${months} ${months === 1 ? "month" : "months"}`;

export const formatDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "-");

export const formatScore = (row) =>
    row?.overallRating !== null && row?.overallRating !== undefined ? `${row.overallRating} / ${row.ratingMax || 5}` : "-";

export const newId = () => Math.random().toString(16).slice(2, 12);

export const emptyTemplate = (category = "appraisal") => ({
    name: "",
    description: "",
    category,
    twoWay: false,
    scoring: "both",
    ratingScale: { max: 5, labels: labelsFor(5) },
    sections: [{ id: newId(), title: "", questions: [{ id: newId(), text: "", type: "rating", answeredBy: defaultAnsweredBy(category), required: true }] }],
});

export const openRecord = async (fetchUrl, id, notify) => {
    // open the tab first so pop-up blockers allow it, then point it at the signed URL
    const tab = window.open("", "_blank");
    try {
        const res = await fetchUrl({ variables: { id }, fetchPolicy: "network-only" });
        const url = res?.data?.getAppraisalRecordUrl?.response?.url;
        if (!url) throw new Error(res?.error?.message || "The record isn't available yet.");
        if (tab) tab.location.href = url; else window.open(url, "_blank");
    } catch (err) {
        if (tab) tab.close();
        notify(err.message, true);
    }
};
